import Icon from '@/components/icons/Icon';
import { useState } from 'react';
import EmployeeInfoModal from '../components/EmployeeInfoModal';
import SelectEmployeeCard, { radioSlot } from '../components/SelectEmployeeCard';
import { ANY_EMPLOYEE_ID, getAnyEmployee } from '../constants';
import type { SelectEmployeeListProps } from './SelectEmployeeList.types';

/**
 * Navigate indicator (Figma node 444-48068): picking a row answers the step and moves the
 * flow on, and the step's own guard redirects away once `sr` is set — so no row is ever
 * re-rendered in a checked state for the visitor to toggle. The drawer is the opposite
 * case and asks for `indicator="select"`.
 *
 * The node's chevron is #A0A8AF (Figma FG Tertiary), which no semantic token maps to in
 * light mode; `fg_secondary` (#506172) is the nearest one that keeps it de-emphasised.
 */
const chevronSlot = <Icon variant="chevron-right" color="fg_secondary" size="sm" />;

/**
 * Presentational employee-list view for `/valj-utovare`: one `SelectEmployeeCard` per
 * bookable employee, plus a "Valfri utövare" row first (unless `showAnyEmployee` is off).
 * A plain `<ul>`/`<li>` list — `SelectEmployeeCard` itself is `role="button"`, so a
 * `radiogroup`/`radio` pairing would mismatch its semantics; native list markup is
 * enough for the list to be announced with its item count.
 *
 * No data fetching or navigation here — the page layer supplies `employees` and reacts
 * to `onPick` to advance the flow.
 */
const SelectEmployeeList = ({
  employees,
  selectedEmployeeId,
  onPick,
  indicator = 'navigate',
  showAnyEmployee = true,
  onOpenEmployeeInfo,
}: SelectEmployeeListProps) => {
  const anyEmployeeSelected = selectedEmployeeId === ANY_EMPLOYEE_ID;
  // Resolved per render, not at module scope, so the copy follows the active locale.
  const anyEmployee = getAnyEmployee();
  const indicatorSlot = (selected: boolean) => (indicator === 'select' ? radioSlot(selected) : chevronSlot);
  const [infoEmployeeId, setInfoEmployeeId] = useState<number | undefined>(undefined);
  const infoEmployee = employees.find((employee) => employee.id === infoEmployeeId);

  const showInfo = (employeeId: number) => {
    if (onOpenEmployeeInfo) onOpenEmployeeInfo(employeeId);
    else setInfoEmployeeId(employeeId);
  };

  return (
    <>
      <ul className="gap-sm flex flex-col">
        {showAnyEmployee && (
          <li>
            {/* "Valfri utövare" is a choice, not a person — no (i) button. */}
            <SelectEmployeeCard
              name={anyEmployee.about.name}
              jobTitle={anyEmployee.about.jobTitle}
              avatarIcon="users-alt"
              selected={anyEmployeeSelected}
              onClick={() => onPick(ANY_EMPLOYEE_ID)}
              rightSlot={indicatorSlot(anyEmployeeSelected)}
            />
          </li>
        )}
        {employees.map((employee) => {
          const selected = selectedEmployeeId === employee.id;
          return (
            <li key={employee.id}>
              <SelectEmployeeCard
                name={employee.name}
                jobTitle={employee.jobTitle}
                avatarUrl={employee.avatarUrl}
                rating={employee.rating}
                selected={selected}
                onClick={() => onPick(employee.id)}
                onInfoClick={employee.info ? () => showInfo(employee.id) : undefined}
                rightSlot={indicatorSlot(selected)}
              />
            </li>
          );
        })}
      </ul>
      {!onOpenEmployeeInfo && (
        <EmployeeInfoModal employee={infoEmployee} onClose={() => setInfoEmployeeId(undefined)} />
      )}
    </>
  );
};

export default SelectEmployeeList;
